import { formatCurrency, formatPercent } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { round } from 'lodash';
import { Observable, lastValueFrom } from 'rxjs';
import {
    IIqTrendlinesFuelStateService,
    TL_FUEL_URL,
    TrendlinesFuelResponse,
} from '../../models';
import { TL_SNAPSHOT_URL } from '@dat/iq-trendlines-snapshot';
import { DatiqAppConfig } from '@dat/datiq-core/models';

type FuelResponse = {
    when: string;
    pricePerGallonUSD: number;
};
type Rate = {
    rate: number;
};

const getCurrencyLabel = (currency: number) => {
    return formatCurrency(currency, 'en-US', '$', 'USD');
};
const getPercentLabel = (currency: number) => {
    return formatPercent(currency, 'en-US', '1.0-2');
};

@Injectable({
    providedIn: 'root',
})
export class IqTrendlinesFuelStateService
    implements IIqTrendlinesFuelStateService
{
    private _baseUrl: string = this._getAnalyticsUrl() + inject(TL_FUEL_URL);
    private _snapshortUrl: string =
        this._getAnalyticsUrl() + inject(TL_SNAPSHOT_URL);

    constructor(
        private _httpClient: HttpClient,
        private _appConfig: DatiqAppConfig,
    ) {}

    private _getAnalyticsUrl(): string {
        return this._appConfig?.coreConfig?.ANALYTICS_BASE_URL ?? '';
    }

    async data(): Promise<{ perGallon: number; percentChange: number }> {
        const perGallon = await this.getCost();
        const percentChange = await this.getPercent();
        return {
            perGallon,
            percentChange,
        };
    }

    cost(): Signal<string> {
        const cost = signal('--');

        this.getCost().then((data) => {
            cost.set(`${data}`);
        });

        return computed(() => {
            return cost();
        });
    }
    percentChange(): Signal<string> {
        const percent = signal('--');

        this.getPercent().then((data) => {
            percent.set(`${data}`);
        });

        return computed(() => {
            return percent();
        });
    }

    private getCost() {
        return lastValueFrom(this._httpClient.get(this._baseUrl)).then(
            (data: FuelResponse) => {
                return data.pricePerGallonUSD;
            },
        );
    }

    private getPercent() {
        return lastValueFrom(this._httpClient.get(this._snapshortUrl)).then(
            (data: TrendlinesFuelResponse) => {
                return round(
                    Number(
                        data?.nationalTrends
                            .weekOverWeekNationalFuelPriceChangeInPercentage,
                    ),
                    2,
                );
            },
        );
    }
}
